import React, { useEffect, useState } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { Grid, InputBase, Typography } from '@mui/material';
import { Elabels } from 'pages/CaseManagementPage/CasesPage/helpers';
import { Resolver, useForm } from 'react-hook-form';
import { IReportAttachment, IReportResultFormValue } from 'models/cases';
import Button from 'components/atoms/Button';
import { EButtonSizesPX, EButtonVariants } from 'constants/Buttons';
import { useNavigate } from 'react-router-dom';
import { FormSelect } from 'components/molecules/FormSelect';
import { EMIMETypes } from 'constants/MIMETypes';
import { FileDropzoneContainer } from 'components/molecules/FormInputFile';
import { FormWyswigEditor } from 'components/molecules/FormWyswigEditor';
import { yupResolver } from '@hookform/resolvers/yup';
import useFormSchema from './schema';
import { useDictionary } from 'models/dictionaryContext';
import { useLang } from 'models/langContext';
import { colors } from 'theme/colors';
import { EIconTypes } from 'constants/Icons';

const initialIntelligenceReportFormValues: IReportResultFormValue = {
  trace: null,
  conclusions: '',
  attachments: null,
};

export interface IInitialValues extends IReportResultFormValue {
  reportAttachments: IReportAttachment[];
}
interface IFormIntelligenceReport {
  isEdit?: boolean;
  onSubmit: (data: IReportResultFormValue) => void;
  initialValues?: IInitialValues;
  prevLink: string;
}

const FormIntelligenceReport = ({
  isEdit = false,
  onSubmit,
  initialValues,
  prevLink,
}: IFormIntelligenceReport) => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const { schema } = useFormSchema();
  const [isLoading, setIsLoading] = useState(false);
  const [initialAttachments, setInitialAttachments] = useState<IReportAttachment[]>();
  const { getIntelligenceReportSourceSystem, intelligenceReportSourceSystem } =
    useDictionary();

  const { selectedLanguage } = useLang();

  useEffect(() => {
    (async function initDictionaries() {
      if (!intelligenceReportSourceSystem) {
        await getIntelligenceReportSourceSystem();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    if (initialValues) {
      const { reportAttachments, ...formValues } = initialValues;
      reset(formValues);
      setInitialAttachments(reportAttachments);
    }
  }, [initialValues]);

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    setError,
    clearErrors,
    getValues,
    watch,
    formState: { errors },
  } = useForm<IReportResultFormValue>({
    defaultValues: initialIntelligenceReportFormValues,
    resolver: yupResolver(schema) as unknown as Resolver<IReportResultFormValue>,
  });

  const watchTrace = watch('trace');

  useEffect(() => {
    if (watchTrace && watchTrace.toString() === 'false') {
      setValue('attachments', []);
      setValue('conclusions', '');
    }
  }, [watchTrace]);

  const submitForm = async (data: IReportResultFormValue) => {
    setIsLoading(true);
    await onSubmit(data);
    setIsLoading(false);
  };

  const handleRemoveAttachment = (attachmentId: string) => {
    const prev = getValues('deletedAttachmentsUniqueIds') ?? [];
    setValue('deletedAttachmentsUniqueIds', [...prev, attachmentId]);
    setInitialAttachments((prev) => prev?.filter((item) => item.uniqueId !== attachmentId));
  };

  return (
    <form onSubmit={handleSubmit(submitForm)} id="search_form">
      <Grid sx={{ gap: '20px' }}>
        <Grid sx={{ gap: '20px' }}>
          <Grid container size={{ xs: 12 }} columnSpacing="20px">
            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
              <FormSelect
                options={[
                  { label: t('general.yes.label'), value: 'true' },
                  { label: t('general.no.label'), value: 'false' },
                ]}
                name="trace"
                setValue={setValue}
                required
                label={t(Elabels.trace)}
                control={control}
                errors={errors}
                id="form_intelligence_report_trace-select"
              />
            </Grid>
            {watchTrace && watchTrace.toString() === 'true' && (
              <>
                <Grid size={{ xs: 12 }}>
                  <FileDropzoneContainer
                    label={t(Elabels.attachment)}
                    name="attachments"
                    setValue={setValue}
                    allowedType={[
                      EMIMETypes.JPEG,
                      EMIMETypes.JPG,
                      EMIMETypes.PNG,
                      EMIMETypes.PDF,
                    ]}
                    multiple
                    errors={errors}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  {!!isEdit && !!initialAttachments?.length && (
                    <Grid
                      sx={{ mt: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}
                    >
                      {initialAttachments.map((item, index) => (
                        <Grid
                          key={index}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'nowrap',
                            gap: '8px',
                          }}
                        >
                          <Grid
                            sx={{
                              border: `1px solid ${colors.gray400}`,
                              borderRadius: '4px',
                              px: '12px',
                              py: '1px',
                              backgroundColor: colors.white,
                              height: '32px',
                              flex: 1,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                            }}
                          >
                            <InputBase
                              value={`${item.filename}.${item.extension.toLowerCase()}`}
                              inputProps={{ 'aria-label': `filename_${index + 1}` }}
                              sx={{
                                width: '100%',
                                '.Mui-disabled': {
                                  color: colors.black,
                                  '-webkit-text-fill-color': `${colors.black} !important`,
                                },
                              }}
                              disabled
                            />
                          </Grid>
                          <Grid>
                            <Button
                              id={`remove_attachment_button_${index}`}
                              variant={EButtonVariants.outlined}
                              icon={EIconTypes.delete}
                              onClick={() => handleRemoveAttachment(item.uniqueId)}
                              sizePX={EButtonSizesPX.px32}
                            />
                          </Grid>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Grid>
                <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
                  <FormWyswigEditor
                    name="conclusions"
                    label={t(Elabels.conclusions)}
                    setValue={setValue}
                    control={control}
                    errors={errors}
                    setError={setError}
                    clearErrors={clearErrors}
                    charsLimit={1000}
                    required
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Grid>

        <Grid container columnSpacing="20px" sx={{ rowGap: 1, mt: 4 }}>
          <Grid size={{ xs: 12, lg: 4 }}>
            <Button
              type="submit"
              fullWidth
              label={t('general.save.button')}
              id="save-button"
              variant={EButtonVariants.contained}
              disabled={isLoading}
            />
          </Grid>

          <Grid size={{ xs: 12, lg: 4 }}>
            <Button
              fullWidth
              label={t('general.cancel.button')}
              id="cancel-button"
              variant={EButtonVariants.outlined}
              onClick={() => {
                navigate(prevLink);
              }}
            />
          </Grid>
        </Grid>
        <Grid sx={{ mt: 1 }}>
          <Typography variant="caption">{t('general.mandatoryField.text')}</Typography>
        </Grid>
      </Grid>
    </form>
  );
};

export default FormIntelligenceReport;
